import React, { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toPng } from 'html-to-image';
import { removeBackground } from '@imgly/background-removal';
import { getMyNftImage, getMyNfts } from '../services/creatorService';

// Match the design spec from C _ Vertical Mark.html
const CARD_W = 1080;
const CARD_H = 1080;
const YELLOW = '#E4CA00';
const BLACK = '#0D0C0D';
const PREVIEW_SCALE = 0.38;
const MODEL_CUTOUT_CONFIG = {
  model: 'isnet_fp16',
  device: 'cpu',
  output: {
    format: 'image/png'
  }
};

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

async function cleanNftCutoutBlob(blob) {
  const sourceUrl = URL.createObjectURL(blob);
  try {
    const img = await loadImage(sourceUrl);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);

    const { width, height } = canvas;
    const imageData = ctx.getImageData(0, 0, width, height);
    const { data } = imageData;
    const total = width * height;
    const visited = new Uint8Array(total);
    const componentId = new Int32Array(total);
    componentId.fill(-1);
    const components = [];
    const isForeground = (pos) => data[pos * 4 + 3] > 24;

    for (let start = 0; start < total; start += 1) {
      if (visited[start] || !isForeground(start)) continue;

      const id = components.length;
      const queue = [start];
      visited[start] = 1;
      componentId[start] = id;
      const component = { area: 0, minX: width, minY: height, maxX: -1, maxY: -1 };

      for (let i = 0; i < queue.length; i += 1) {
        const pos = queue[i];
        const x = pos % width;
        const y = Math.floor(pos / width);
        component.area += 1;
        component.minX = Math.min(component.minX, x);
        component.minY = Math.min(component.minY, y);
        component.maxX = Math.max(component.maxX, x);
        component.maxY = Math.max(component.maxY, y);

        const neighbors = [
          x > 0 ? pos - 1 : -1,
          x < width - 1 ? pos + 1 : -1,
          y > 0 ? pos - width : -1,
          y < height - 1 ? pos + width : -1
        ];
        neighbors.forEach((next) => {
          if (next < 0 || visited[next] || !isForeground(next)) return;
          visited[next] = 1;
          componentId[next] = id;
          queue.push(next);
        });
      }
      components.push(component);
    }

    if (components.length === 0) return blob;

    const largest = components.reduce((best, component, index) => (
      component.area > components[best].area ? index : best
    ), 0);
    const main = components[largest];
    const closeToMain = (component) => (
      component.maxX >= main.minX - width * 0.12 &&
      component.minX <= main.maxX + width * 0.12 &&
      component.maxY >= main.minY - height * 0.12 &&
      component.minY <= main.maxY + height * 0.12
    );
    const keep = new Set();
    components.forEach((component, index) => {
      if (
        index === largest ||
        component.area >= Math.max(1800, main.area * 0.018) ||
        (component.area >= 500 && closeToMain(component))
      ) {
        keep.add(index);
      }
    });

    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    for (let pos = 0; pos < total; pos += 1) {
      const idx = pos * 4;
      if (data[idx + 3] <= 24 || !keep.has(componentId[pos])) {
        data[idx + 3] = 0;
        continue;
      }
      const x = pos % width;
      const y = Math.floor(pos / width);
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }

    if (maxX < minX || maxY < minY) return blob;

    ctx.putImageData(imageData, 0, 0);
    const pad = 18;
    const sx = Math.max(0, minX - pad);
    const sy = Math.max(0, minY - pad);
    const sw = Math.min(width - sx, maxX - minX + 1 + pad * 2);
    const sh = Math.min(height - sy, maxY - minY + 1 + pad * 2);
    const cropped = document.createElement('canvas');
    cropped.width = sw;
    cropped.height = sh;
    cropped.getContext('2d').drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh);
    const cleaned = await new Promise((resolve) => cropped.toBlob(resolve, 'image/png'));
    return cleaned || blob;
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
}

const isStudioBackgroundPixel = (data, idx) => {
  const r = data[idx];
  const g = data[idx + 1];
  const b = data[idx + 2];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const avg = (r + g + b) / 3;

  return avg > 92 && avg < 238 && max - min < 36;
};

const isStudioHaloPixel = (data, idx) => {
  const r = data[idx];
  const g = data[idx + 1];
  const b = data[idx + 2];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const avg = (r + g + b) / 3;

  return avg > 56 && avg < 245 && max - min < 48;
};

async function createNftCutoutUrl(blob) {
  try {
    const cutoutBlob = await cleanNftCutoutBlob(await removeBackground(blob, MODEL_CUTOUT_CONFIG));
    return URL.createObjectURL(cutoutBlob);
  } catch {
    return createHeuristicNftCutoutUrl(blob);
  }
}

async function createHeuristicNftCutoutUrl(blob) {
  const sourceUrl = URL.createObjectURL(blob);
  try {
    const img = await loadImage(sourceUrl);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);

    const { width, height } = canvas;
    const imageData = ctx.getImageData(0, 0, width, height);
    const { data } = imageData;
    const visited = new Uint8Array(width * height);
    const queue = [];
    const push = (x, y) => {
      if (x < 0 || y < 0 || x >= width || y >= height) return;
      const pos = y * width + x;
      if (visited[pos]) return;
      const idx = pos * 4;
      if (!isStudioBackgroundPixel(data, idx)) return;
      visited[pos] = 1;
      queue.push(pos);
    };

    for (let x = 0; x < width; x += 1) {
      push(x, 0);
      push(x, height - 1);
    }
    for (let y = 1; y < height - 1; y += 1) {
      push(0, y);
      push(width - 1, y);
    }

    for (let i = 0; i < queue.length; i += 1) {
      const pos = queue[i];
      const x = pos % width;
      const y = Math.floor(pos / width);
      const idx = pos * 4;
      data[idx + 3] = 0;
      push(x + 1, y);
      push(x - 1, y);
      push(x, y + 1);
      push(x, y - 1);
    }

    for (let pass = 0; pass < 4; pass += 1) {
      const clear = [];
      for (let y = 1; y < height - 1; y += 1) {
        for (let x = 1; x < width - 1; x += 1) {
          const pos = y * width + x;
          const idx = pos * 4;
          if (data[idx + 3] === 0 || !isStudioHaloPixel(data, idx)) continue;

          const transparentNeighbors = [
            data[((y - 1) * width + x) * 4 + 3],
            data[((y + 1) * width + x) * 4 + 3],
            data[(y * width + x - 1) * 4 + 3],
            data[(y * width + x + 1) * 4 + 3]
          ].filter((alpha) => alpha === 0).length;

          if (transparentNeighbors >= 2) clear.push(idx);
        }
      }
      if (clear.length === 0) break;
      clear.forEach((idx) => { data[idx + 3] = 0; });
    }

    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const alpha = data[(y * width + x) * 4 + 3];
        if (alpha > 0) {
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
    }

    if (maxX < minX || maxY < minY) {
      return URL.createObjectURL(blob);
    }

    ctx.putImageData(imageData, 0, 0);
    const pad = 12;
    const sx = Math.max(0, minX - pad);
    const sy = Math.max(0, minY - pad);
    const sw = Math.min(width - sx, maxX - minX + 1 + pad * 2);
    const sh = Math.min(height - sy, maxY - minY + 1 + pad * 2);
    const cropped = document.createElement('canvas');
    cropped.width = sw;
    cropped.height = sh;
    cropped.getContext('2d').drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh);

    const cutoutBlob = await new Promise((resolve) => cropped.toBlob(resolve, 'image/png'));
    return URL.createObjectURL(cutoutBlob || blob);
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
}

const formatAmount = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return '$0';
  return `$${Math.round(v).toLocaleString()}`;
};

const Card = React.forwardRef(function Card({ amountText, image }, ref) {
  return (
    <div
      ref={ref}
      style={{
        width: CARD_W,
        height: CARD_H,
        background: BLACK,
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
      }}
    >
      {/* Top "TAKEOVER x BRICKTOPIANS" line — Inter 21/800 with wide letter-spacing */}
      <div style={{ position: 'absolute', top: 54, left: 72, display: 'flex', alignItems: 'center', gap: 12, zIndex: 2, fontFamily: "'Inter', sans-serif" }}>
        <span style={{ color: '#fff', fontSize: 21, fontWeight: 800, letterSpacing: '5.04px', lineHeight: '21px' }}>TAKEOVER</span>
        <span style={{ color: YELLOW, fontSize: 30, fontWeight: 200, lineHeight: '30px' }}>x</span>
        <span style={{ color: '#fff', fontSize: 21, fontWeight: 800, letterSpacing: '5.04px', lineHeight: '21px' }}>BRICKTOPIANS</span>
      </div>

      {/* Left text block — Saira Extra Condensed 900, vertically centered */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 72,
          width: 470,
          height: CARD_H,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          zIndex: 2,
          fontFamily: "'Saira Extra Condensed', sans-serif"
        }}
      >
        <div style={{ color: '#fff', fontSize: 116, fontWeight: 900, lineHeight: '104.4px', letterSpacing: '-2.32px' }}>This brick</div>
        <div style={{ color: '#fff', fontSize: 116, fontWeight: 900, lineHeight: '104.4px', letterSpacing: '-2.32px', marginBottom: 36 }}>made me</div>
        <div style={{ background: YELLOW, height: 12, width: 300, marginBottom: 36 }} />
        <div style={{ color: YELLOW, fontSize: 250, fontWeight: 900, lineHeight: '205px', letterSpacing: '-5px' }}>{amountText}</div>
      </div>

      {/* NFT image on the right side — exact design spec: starts at x=557 so it
          does NOT overlap the text block (text ends at x=542). Width 708 means
          the image overflows the right edge by 185px which is intentional. */}
      {image && (
        <img
          src={image}
          alt=""
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            top: 180,
            left: 557,
            width: 708,
            height: 900,
            objectFit: 'cover',
            zIndex: 1
          }}
        />
      )}

      {/* Bottom-left yellow square */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, width: 72, height: 72, background: YELLOW, zIndex: 2 }} />
    </div>
  );
});

export default function SocialCardModal({ open, onClose, totalEarned, handle, nftHolderVerified }) {
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTokenId, setSelectedTokenId] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [cardImageUrl, setCardImageUrl] = useState(null);
  const cardRef = useRef(null);
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  const restoreFocusRef = useRef(true);
  const titleId = useId();
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const trigger = document.activeElement;
    const dialog = dialogRef.current;
    const previousOverflow = document.body.style.overflow;
    restoreFocusRef.current = true;
    document.body.style.overflow = 'hidden';
    const focusable = () => Array.from(dialog.querySelectorAll('button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled),[tabindex="0"]'))
      .filter((element) => !element.closest('[hidden],[aria-hidden="true"]'));
    (focusable()[0] || dialog).focus();
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeRef.current();
      }
      if (event.key === 'Tab') {
        const elements = focusable();
        const first = elements[0] || dialog;
        const last = elements[elements.length - 1] || dialog;
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog)) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    const containFocus = (event) => {
      if (!dialog.contains(event.target)) (focusable()[0] || dialog).focus();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('focusin', containFocus);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focusin', containFocus);
      document.body.style.overflow = previousOverflow;
      if (restoreFocusRef.current && trigger?.isConnected) trigger.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open || !nftHolderVerified) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNfts([]);
    setSelectedTokenId(null);
    getMyNfts()
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data?.nfts) ? res.data.nfts : [];
        setNfts(list);
        if (list.length > 0) setSelectedTokenId(list[0].tokenId);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.error || 'Failed to load your NFTs.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, nftHolderVerified]);

  useEffect(() => {
    if (!open || !selectedTokenId) {
      setCardImageUrl(null);
      return undefined;
    }

    let cancelled = false;
    let objectUrl = null;
    setCardImageUrl(null);

    // Fast path: use the raw NFT image directly. The backend endpoint proxies
    // it through our own origin, so wrapping it in a blob URL gives us a
    // same-origin image that html-to-image can rasterize without tainting
    // the canvas. Client-side background-removal (the previous createNftCutoutUrl
    // call) took ~10-30s the first time due to the WASM ML model, so it's off
    // by default. The design position doesn't require a cutout to look right.
    getMyNftImage(selectedTokenId)
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setCardImageUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setCardImageUrl(null);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, selectedTokenId]);

  if (!open) return null;

  const selected = nfts.find((n) => n.tokenId === selectedTokenId);
  const amountText = formatAmount(totalEarned);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    setError(null);
    try {
      // Wait for fonts (Saira Extra Condensed, Inter) to finish loading before
      // rasterizing — otherwise the PNG falls back to system sans-serif and
      // the proportions of the big text are wrong.
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        width: CARD_W,
        height: CARD_H,
        pixelRatio: 1
      });
      const link = document.createElement('a');
      link.download = `bricktopians-card${selected?.tokenId ? `-${selected.tokenId}` : ''}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      setError('Could not generate the image. Try a different NFT or reload the page.');
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = () => {
    const text = `Check out my Bricktopians card from @WeTakeover`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className="portal-modal-overlay"
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.85)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        overflow: 'auto'
      }}
      onClick={onClose}
    >
      <div
        className="portal-modal"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{
          background: '#141414',
          color: '#fff',
          padding: 24,
          borderRadius: 8,
          maxWidth: 640,
          width: '100%',
          maxHeight: '95vh',
          overflow: 'auto',
          fontFamily: 'Inter, -apple-system, sans-serif'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 id={titleId} style={{ margin: 0, fontSize: 16, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Generate Social Card</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 24, cursor: 'pointer', lineHeight: 1, padding: 4 }}
          >
            ×
          </button>
        </div>

        {!nftHolderVerified && (
          <div style={{ padding: '24px 0' }}>
            <div style={{ fontSize: 14, color: '#fff', fontWeight: 600, marginBottom: 12 }}>
              Verify your Bricktopians NFT first
            </div>
            <div style={{ fontSize: 12, color: '#aaa', lineHeight: 1.6, marginBottom: 20 }}>
              The social card pulls from your verified Bricktopians wallet. Head to Settings, paste your wallet address on the Bricktopian NFT row, and we'll confirm you hold a brick (you also need to be verified by the Vulcan bot in our Discord). Once verified, come back here and pick the brick you want on your card.
            </div>
            <Link
              to="/settings"
              onClick={(event) => {
                if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                restoreFocusRef.current = false;
                onClose();
              }}
              style={{
                display: 'inline-block',
                background: YELLOW,
                color: '#000',
                padding: '10px 16px',
                borderRadius: 4,
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                fontWeight: 800,
                textDecoration: 'none'
              }}
            >
              Go to Settings to verify
            </Link>
          </div>
        )}

        {nftHolderVerified && loading && (
          <div style={{ padding: 32, textAlign: 'center', color: '#999', fontSize: 12 }}>Loading your NFTs…</div>
        )}

        {nftHolderVerified && error && (
          <div style={{ color: '#ff6b6b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
            {error}
          </div>
        )}

        {nftHolderVerified && !loading && !error && nfts.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: '#999', fontSize: 12 }}>
            No Bricktopians NFTs found in your verified wallet.
          </div>
        )}

        {nftHolderVerified && nfts.length > 0 && (
          <>
            <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
              Pick a brick · {nfts.length}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                gap: 8,
                marginBottom: 24,
                maxHeight: 200,
                overflowY: 'auto',
                paddingRight: 4
              }}
            >
              {nfts.map((nft) => {
                const isSelected = nft.tokenId === selectedTokenId;
                return (
                  <button
                    key={nft.tokenId}
                    onClick={() => setSelectedTokenId(nft.tokenId)}
                    title={nft.name || `#${nft.tokenId}`}
                    style={{
                      padding: 0,
                      border: isSelected ? `2px solid ${YELLOW}` : '2px solid transparent',
                      background: '#0a0a0a',
                      borderRadius: 4,
                      cursor: 'pointer',
                      overflow: 'hidden'
                    }}
                  >
                    <img
                      src={nft.thumbnail || nft.image}
                      alt={nft.name || `Bricktopian #${nft.tokenId}`}
                      style={{ width: '100%', height: 80, objectFit: 'cover', display: 'block' }}
                    />
                  </button>
                );
              })}
            </div>

            <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
              Preview
            </div>
            <div
              style={{
                width: CARD_W * PREVIEW_SCALE,
                height: CARD_H * PREVIEW_SCALE,
                maxWidth: '100%',
                margin: '0 auto 20px',
                overflow: 'hidden',
                borderRadius: 4,
                background: BLACK
              }}
            >
              <div
                style={{
                  width: CARD_W,
                  height: CARD_H,
                  transform: `scale(${PREVIEW_SCALE})`,
                  transformOrigin: 'top left'
                }}
              >
                <Card amountText={amountText} image={cardImageUrl || selected?.image} />
              </div>
            </div>

            {/* Hidden full-size card used for PNG export */}
            <div
              style={{
                position: 'fixed',
                top: -CARD_H - 100,
                left: -CARD_W - 100,
                pointerEvents: 'none',
                opacity: 0
              }}
              aria-hidden="true"
            >
              <Card ref={cardRef} amountText={amountText} image={cardImageUrl || selected?.image} />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                onClick={handleShare}
                style={{
                  background: 'transparent',
                  color: '#fff',
                  border: '1px solid #444',
                  padding: '10px 16px',
                  borderRadius: 4,
                  fontSize: 11,
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontWeight: 700
                }}
              >
                Share on X
              </button>
              <button
                onClick={handleDownload}
                disabled={downloading || !selected || !(cardImageUrl || selected?.image)}
                style={{
                  background: YELLOW,
                  color: '#000',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: 4,
                  fontSize: 11,
                  cursor: downloading ? 'wait' : 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontWeight: 800,
                  opacity: downloading || !selected || !(cardImageUrl || selected?.image) ? 0.6 : 1
                }}
              >
                {downloading ? 'Generating…' : 'Download PNG'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
